import "./consensusActivityQuestionView.css"
import React, {useEffect} from 'react';
import { useState, useRef } from "react";
import { logError, LogCategory } from "utils/sentryLogger";
import { useHistory, useLocation } from "react-router-dom";
import Select from "react-select";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faPlus, faSpinner } from "@fortawesome/free-solid-svg-icons";
import { request } from "../../../../../../../hooks/useOkoApi/useOkoApi";
import { useAuthUserData } from "hooks/useAuthUserData/useAuthUserData";
import { useStandards } from "hooks/useStandards/useStandards";
import { buildSelectOptions} from "utils/standards";
import { ConsensusQuestion } from "./ConsensusQuestion";
import Accordion from "react-bootstrap/Accordion";
import { EditConsensusQuestionModal } from "./EditConsensusQuestionModal";
import { buildEmptyChallenge, CHALLENGE_TYPE_FILTER_OPTIONS, getIllustrationOptions } from "utils/challenge";
import { constants, generateRoutePath } from "utils";

const PAGE_SIZE = 50;

const STATUS_FILTER_OPTIONS = [
  { value: "all", label: "All statuses" },
  { value: "active", label: "Active only" },
  { value: "inactive", label: "Inactive only" },
];

const UUID_RE = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

// LC benchmark filter options (OKO2-3551).
const LC_TAG_COUNT_OPTIONS = [
  { value: "all", label: "LC Tag Count: All" },
  { value: "not_reviewed", label: "Not Reviewed" },
  { value: "null", label: "Null" },
  { value: "one", label: "One" },
  { value: "two_plus", label: "2 or more" },
];

const QUALITY_GATE_OPTIONS = [
  { value: "all", label: "Quality Gate: All" },
  { value: "pass", label: "Pass" },
  { value: "reject", label: "Reject" },
];

const SOFT_FLAG_OPTIONS = [
  { value: "all", label: "Soft Flag: All" },
  { value: "null", label: "Null" },
  { value: "not_null", label: "Not Null" },
];

const REJECT_TAG_OPTIONS = [
  { value: "none", label: "None" },
  { value: "any", label: "Any" },
  { value: "BAD_KEY", label: "BAD_KEY" },
  { value: "BAD_IMAGE", label: "BAD_IMAGE" },
  { value: "UNREADABLE", label: "UNREADABLE" },
  { value: "MISSING_CONTEXT", label: "MISSING_CONTEXT" },
];

// "<model>|<prompt_version>" round-trips as the run select's value.
const runToValue = (run) => (run ? `${run.model}|${run.prompt_version}` : null);

// Translate the UI filter state into the /questions/fetch/challenges query.
const buildChallengeParams = ({
  prefix,
  source,
  format,
  status,
  illustration,
  misconception,
  run,
  lc,
  lcTagCount,
  qualityGate,
  softFlag,
  rejectTags,
  offset,
}) => {
  const params = [`offset=${offset}`, `limit=${PAGE_SIZE}`];
  if (format && format !== "all") params.push(`challenge_format=${encodeURIComponent(format)}`);
  if (status && status !== "all") params.push(`challenge_status=${encodeURIComponent(status)}`);
  if (prefix) params.push(`standard_prefix=${encodeURIComponent(prefix)}`);
  if (source) params.push(`source=${encodeURIComponent(source)}`);
  if (misconception && misconception !== "all") {
    params.push(`misconception=${encodeURIComponent(misconception)}`);
  }
  if (illustration === "has") params.push("has_image=true");
  else if (illustration === "none") params.push("has_image=false");
  else if (illustration && illustration.startsWith("type:")) {
    params.push(`illustration_type=${encodeURIComponent(illustration.slice(5))}`);
  }
  if (run) params.push(`run=${encodeURIComponent(run)}`);
  if (lc && lc.trim()) params.push(`lc=${encodeURIComponent(lc.trim())}`);
  if (lcTagCount && lcTagCount !== "all") {
    params.push(`lc_tag_count=${encodeURIComponent(lcTagCount)}`);
  }
  if (qualityGate && qualityGate !== "all") {
    params.push(`quality_gate=${encodeURIComponent(qualityGate)}`);
  }
  if (softFlag && softFlag !== "all") params.push(`soft_flag=${encodeURIComponent(softFlag)}`);
  if (rejectTags && rejectTags.length > 0) {
    params.push(`reject_tags=${encodeURIComponent(rejectTags.join(","))}`);
  }
  return params.join("&");
};

export const ConsensusQuestionView = () => {
  const { authUserData, loading } = useAuthUserData();
  const history = useHistory();
  const location = useLocation();
  const standardsData = useStandards();
  const [questionsData, setQuestionsData] = useState([]);
  const [total, setTotal] = useState(0);
  const [offset, setOffset] = useState(0);
  const [prefix, setPrefix] = useState("");
  const [selectedSource, setSelectedSource] = useState(null);
  const [statusFilter, setStatusFilter] = useState("all");
  const [illustrationFilter, setIllustrationFilter] = useState("all");
  const [misconceptionFilter, setMisconceptionFilter] = useState("all");
  const [illustrationTypes, setIllustrationTypes] = useState([]);
  const [misconceptions, setMisconceptions] = useState([]);
  const [uidInput, setUidInput] = useState("");
  const [uidError, setUidError] = useState("");
  const [fetchError, setFetchError] = useState("");
  const [isUidResult, setIsUidResult] = useState(false);
  const [sourcesData, setSourcesData] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedChallengeType, setSelectedChallengeType] = useState("all");
  const [needsRefresh, setNeedsRefresh] = useState(false);

  // LC benchmark filters (OKO2-3551).
  const [selectedRun, setSelectedRun] = useState(null);
  const [availableRuns, setAvailableRuns] = useState([]);
  const [lcInput, setLcInput] = useState("");
  const [lcTagCountFilter, setLcTagCountFilter] = useState("all");
  const [qualityGateFilter, setQualityGateFilter] = useState("all");
  const [softFlagFilter, setSoftFlagFilter] = useState("all");
  const [rejectTagsFilter, setRejectTagsFilter] = useState([]);
  const runInitializedRef = useRef(false);
  const lcMountedRef = useRef(false);
  const lcDebounceRef = useRef(null);

  // Snapshot the committed filter state for a fetch; handlers override the one
  // field they change so we never read a stale value mid-update.
  const currentFilters = () => ({
    prefix,
    source: selectedSource,
    format: selectedChallengeType,
    status: statusFilter,
    illustration: illustrationFilter,
    misconception: misconceptionFilter,
    run: selectedRun,
    lc: lcInput,
    lcTagCount: lcTagCountFilter,
    qualityGate: qualityGateFilter,
    softFlag: softFlagFilter,
    rejectTags: rejectTagsFilter,
    offset,
  });

  const getChallenges = async (args) => {
    try {
      setIsLoading(true);
      setUidError("");
      setFetchError("");
      setIsUidResult(false);
      const qs = buildChallengeParams(args);
      const resp = await request(`questions/fetch/challenges?${qs}`, { method: "GET" });
      setQuestionsData(resp.items || []);
      setTotal(resp.total || 0);
      setIllustrationTypes(resp.illustration_types || []);
      setMisconceptions(resp.misconceptions || []);
      setAvailableRuns(resp.available_runs || []);
      if (!runInitializedRef.current) {
        runInitializedRef.current = true;
        if (resp.selected_run) setSelectedRun(runToValue(resp.selected_run));
      }
      setOffset(args.offset);
      setIsLoading(false);
    } catch (e) {
      setIsLoading(false);
      setQuestionsData([]);
      setTotal(0);
      setFetchError("Failed to load challenges. Try narrowing the filters and retry.");
      logError("Failed to fetch consensus questions", e, {
        category: LogCategory.ACTIVITY,
        activityType: "consensus",
      });
    }
  };

  const handleUidLookup = async (idOverride) => {
    setUidError("");
    setFetchError("");
    const rawId = typeof idOverride === "string" ? idOverride : uidInput;
    const trimmed = (rawId || "").trim();
    if (!UUID_RE.test(trimmed)) {
      setUidError("Not a valid UUID");
      return;
    }
    setUidInput(trimmed);
    const typesToTry =
      selectedChallengeType === "all"
        ? ["multiplechoice", "openresponse"]
        : [selectedChallengeType];
    setIsLoading(true);
    let lastError = null;
    for (const type of typesToTry) {
      try {
        const item = await request(`questions/${type}/${trimmed}`, { method: "GET" });
        setQuestionsData([item]);
        setTotal(1);
        setOffset(0);
        setIsUidResult(true);
        setIsLoading(false);
        return;
      } catch (e) {
        lastError = e;
      }
    }
    setIsLoading(false);
    const typeLabel = selectedChallengeType === "all" ? "any" : selectedChallengeType;
    setUidError(`Lookup failed (404 or non-${typeLabel} type?)`);
    logError("Failed UID lookup", lastError, {
      category: LogCategory.ACTIVITY,
      activityType: "consensus",
    });
  };

  useEffect(() => {
    if (needsRefresh) {
      getChallenges(currentFilters());
      setNeedsRefresh(false);
    }
  }, [needsRefresh]);

  useEffect(() => {
    (async () => {
      try {
        const sources = await request("questions/sources", { method: "GET" });
        setSourcesData(sources || []);
      } catch (e) {
        logError("Failed to fetch challenge sources", e, {
          category: LogCategory.ACTIVITY,
          activityType: "consensus",
        });
      }
    })();

    const searchParams = new URLSearchParams(location.search);
    const questionId = (searchParams.get("question_id") || "").trim();
    if (UUID_RE.test(questionId)) {
      handleUidLookup(questionId);
    } else {
      getChallenges({
        prefix: "",
        source: null,
        format: "all",
        status: "all",
        illustration: "all",
        misconception: "all",
        run: null,
        lc: "",
        lcTagCount: "all",
        qualityGate: "all",
        softFlag: "all",
        rejectTags: [],
        offset: 0,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Debounce the LC text filter; skip the effect on mount since the initial
  // fetch above already covers the empty default.
  useEffect(() => {
    if (!lcMountedRef.current) {
      lcMountedRef.current = true;
      return;
    }
    if (lcDebounceRef.current) clearTimeout(lcDebounceRef.current);
    lcDebounceRef.current = setTimeout(() => {
      getChallenges({ ...currentFilters(), lc: lcInput, offset: 0 });
    }, 350);
    return () => {
      if (lcDebounceRef.current) clearTimeout(lcDebounceRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lcInput]);

  const getMisconceptionOptions = () => {
    const options = [...misconceptions].sort().map((m) => ({ value: m, label: m }));
    return [{ value: "all", label: "All misconceptions" }, ...options];
  };

  const getStandardsOptions = () => {
    const options = buildSelectOptions(standardsData);
    options.unshift({ value: null, label: "All available standards" });
    return options;
  };

  const getRunOptions = () =>
    availableRuns.map((r) => ({
      value: runToValue(r),
      label: `${r.model} · ${r.prompt_version} (${r.question_count})`,
    }));

  const getSourceOptions = () => {
    const options = sourcesData.map((s) => ({
      value: s.source,
      label: `${s.source}  (${s.mc_count} MC / ${s.or_count} OR)`,
    }));
    options.unshift({ value: null, label: "All item sets" });
    return options;
  };

  // Any filter change resets to the first page.
  const handleOnStandardsChange = (target) => {
    setPrefix(target.value);
    getChallenges({ ...currentFilters(), prefix: target.value, offset: 0 });
  };

  const handleOnFormatChange = (target) => {
    setSelectedChallengeType(target.value);
    getChallenges({ ...currentFilters(), format: target.value, offset: 0 });
  };

  const handleOnSourceChange = (target) => {
    setSelectedSource(target.value);
    getChallenges({ ...currentFilters(), source: target.value, offset: 0 });
  };

  const handleOnStatusFilterChange = (target) => {
    setStatusFilter(target.value);
    getChallenges({ ...currentFilters(), status: target.value, offset: 0 });
  };

  const handleOnIllustrationChange = (target) => {
    setIllustrationFilter(target.value);
    getChallenges({ ...currentFilters(), illustration: target.value, offset: 0 });
  };

  const handleOnMisconceptionChange = (target) => {
    setMisconceptionFilter(target.value);
    getChallenges({ ...currentFilters(), misconception: target.value, offset: 0 });
  };

  const handleOnRunChange = (target) => {
    setSelectedRun(target.value);
    getChallenges({ ...currentFilters(), run: target.value, offset: 0 });
  };

  const handleOnLcTagCountChange = (target) => {
    setLcTagCountFilter(target.value);
    getChallenges({ ...currentFilters(), lcTagCount: target.value, offset: 0 });
  };

  const handleOnQualityGateChange = (target) => {
    setQualityGateFilter(target.value);
    getChallenges({ ...currentFilters(), qualityGate: target.value, offset: 0 });
  };

  const handleOnSoftFlagChange = (target) => {
    setSoftFlagFilter(target.value);
    getChallenges({ ...currentFilters(), softFlag: target.value, offset: 0 });
  };

  const handleOnRejectTagsChange = (selected) => {
    const values = (selected || []).map((o) => o.value);
    setRejectTagsFilter(values);
    getChallenges({ ...currentFilters(), rejectTags: values, offset: 0 });
  };

  const goToPage = (newOffset) => {
    getChallenges({ ...currentFilters(), offset: newOffset });
  };

  const styles = {
    control: (css) => ({
      ...css,
      width: "calc(100vw - 312px - 44px)",
      maxWidth: "800px",
    }),
  };

  const handleAddNewChallengeButton = () => {
    setIsModalOpen(true);
  };

  const newChallengeButton = () => (
    <div className="new-challenge-button">
      <button
        style={{ borderRadius: "100px", borderWidth: "1px", marginLeft: "18px" }}
        onClick={handleAddNewChallengeButton}
      >
        <FontAwesomeIcon icon={faPlus} />
        &nbsp;Add new challenge item
      </button>
    </div>
  );

  const placeholderPrefix = prefix ? `for standard ${prefix}` : "";
  const rangeStart = total === 0 ? 0 : offset + 1;
  const rangeEnd = Math.min(offset + questionsData.length, total);
  const hasPrev = !isUidResult && offset > 0;
  const hasNext = !isUidResult && offset + PAGE_SIZE < total;

  const pager = () => (
    <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
      <button
        onClick={() => goToPage(Math.max(0, offset - PAGE_SIZE))}
        disabled={isLoading || !hasPrev}
        style={{ borderRadius: "4px", borderWidth: "1px", padding: "4px 12px" }}
      >
        Prev
      </button>
      <span>
        {isUidResult
          ? "UID result"
          : `Showing ${rangeStart}-${rangeEnd} of ${total} ${placeholderPrefix}`}
      </span>
      <button
        onClick={() => goToPage(offset + PAGE_SIZE)}
        disabled={isLoading || !hasNext}
        style={{ borderRadius: "4px", borderWidth: "1px", padding: "4px 12px" }}
      >
        Next
      </button>
    </div>
  );

  if (loading) return <></>;
  if (authUserData.isAnonymous) history.push(generateRoutePath(constants.ADMIN_LOGIN_ROUTE_NAME));
  return (
    <div>
      {isLoading && <div style={{ left: "50%", position: "absolute", top: "50%" }}>
        <FontAwesomeIcon icon={faSpinner} spin size="5x" />
      </div>}
      <div style={{
        alignItems: "center",
        display: "flex",
        flexDirection: "column",
        justifySelf: "center",
        marginBottom: "15px",
        width: "calc(100vw - 312px - 44px)",
        maxWidth: "800px",
        gap: "8px",
      }}>
        {pager()}

        <Select
          isDisabled={isLoading}
          isSearchable
          placeholder="Select standard..."
          options={getStandardsOptions()}
          onChange={handleOnStandardsChange}
          styles={styles}
        />

        <Select
          isDisabled={isLoading}
          value={CHALLENGE_TYPE_FILTER_OPTIONS.find((o) => o.value === selectedChallengeType)}
          isSearchable
          placeholder="Select challenge type..."
          options={CHALLENGE_TYPE_FILTER_OPTIONS}
          onChange={handleOnFormatChange}
          styles={styles}
        />

        <Select
          isDisabled={isLoading}
          isSearchable
          value={getSourceOptions().find((o) => o.value === selectedSource) || null}
          placeholder="Filter by item set / source..."
          options={getSourceOptions()}
          onChange={handleOnSourceChange}
          styles={styles}
        />

        <Select
          isDisabled={isLoading}
          value={STATUS_FILTER_OPTIONS.find((o) => o.value === statusFilter)}
          placeholder="Filter by status..."
          options={STATUS_FILTER_OPTIONS}
          onChange={handleOnStatusFilterChange}
          styles={styles}
        />

        <Select
          isDisabled={isLoading}
          value={getIllustrationOptions(illustrationTypes).find((o) => o.value === illustrationFilter)}
          placeholder="Filter by illustration..."
          options={getIllustrationOptions(illustrationTypes)}
          onChange={handleOnIllustrationChange}
          styles={styles}
        />

        <Select
          isDisabled={isLoading}
          value={getMisconceptionOptions().find((o) => o.value === misconceptionFilter)}
          placeholder="Filter by misconception..."
          options={getMisconceptionOptions()}
          onChange={handleOnMisconceptionChange}
          styles={styles}
        />

        <Select
          isDisabled={isLoading}
          value={getRunOptions().find((o) => o.value === selectedRun) || null}
          isSearchable
          placeholder="Select benchmark run..."
          options={getRunOptions()}
          onChange={handleOnRunChange}
          styles={styles}
        />

        <input
          type="text"
          placeholder="Filter by LC (id or name)..."
          value={lcInput}
          onChange={(e) => setLcInput(e.target.value)}
          disabled={isLoading}
          style={{
            width: "calc(100vw - 312px - 44px)",
            maxWidth: "800px",
            padding: "8px 12px",
            borderRadius: "4px",
            border: "1px solid #ccc",
          }}
        />

        <Select
          isDisabled={isLoading}
          value={LC_TAG_COUNT_OPTIONS.find((o) => o.value === lcTagCountFilter)}
          placeholder="Filter by LC tag count..."
          options={LC_TAG_COUNT_OPTIONS}
          onChange={handleOnLcTagCountChange}
          styles={styles}
        />

        <Select
          isDisabled={isLoading}
          value={QUALITY_GATE_OPTIONS.find((o) => o.value === qualityGateFilter)}
          placeholder="Filter by quality gate..."
          options={QUALITY_GATE_OPTIONS}
          onChange={handleOnQualityGateChange}
          styles={styles}
        />

        <Select
          isDisabled={isLoading}
          value={SOFT_FLAG_OPTIONS.find((o) => o.value === softFlagFilter)}
          placeholder="Filter by soft flag..."
          options={SOFT_FLAG_OPTIONS}
          onChange={handleOnSoftFlagChange}
          styles={styles}
        />

        <Select
          isDisabled={isLoading}
          isMulti
          value={REJECT_TAG_OPTIONS.filter((o) => rejectTagsFilter.includes(o.value))}
          placeholder="Filter by reject tags..."
          options={REJECT_TAG_OPTIONS}
          onChange={handleOnRejectTagsChange}
          styles={styles}
        />

        <div style={{ display: "flex", flexDirection: "row", gap: "8px", width: "100%", maxWidth: "800px" }}>
          <input
            type="text"
            placeholder="Paste a question UUID to jump to it"
            value={uidInput}
            onChange={(e) => setUidInput(e.target.value)}
            style={{ flexGrow: 1, padding: "6px 10px", borderRadius: "4px", border: "1px solid #ccc" }}
          />
          <button
            onClick={handleUidLookup}
            disabled={isLoading || !uidInput}
            style={{ borderRadius: "4px", borderWidth: "1px", padding: "6px 14px" }}
          >
            Jump to UID
          </button>
        </div>
        {uidError && <p style={{ color: "red", margin: 0 }}>{uidError}</p>}
        {fetchError && <p style={{ color: "red", margin: 0 }}>{fetchError}</p>}
      </div>
      {!isLoading && newChallengeButton()}
      {!isLoading && <Accordion style={{ display: "flex", flexDirection: "column", gap: "8px", padding: "8px" }}>
        {questionsData.map((challenge, index) => (
          <ConsensusQuestion key={challenge.id} challenge={challenge} index={index} showHeader setNeedsRefresh={setNeedsRefresh} />
        ))}
      </Accordion>
      }
      {!isLoading && !isUidResult && total > PAGE_SIZE && (
        <div style={{ display: "flex", justifyContent: "center", padding: "8px" }}>{pager()}</div>
      )}
      <EditConsensusQuestionModal
        challenge={buildEmptyChallenge(selectedChallengeType !== "openresponse")}
        isModalOpen={isModalOpen}
        modalTitle="Create New Challenge"
        setIsModalOpen={setIsModalOpen}
        setNeedsRefresh={setNeedsRefresh}
      />
    </div>
  );
};
